/** CONSTRAINT: the 2 000 € the Coluche sentences quote, in minor units. */
export const COLUCHE_THRESHOLD_MINOR = 200000;
// Integer percentages, never floats: a binary float rate is inexact, so its floor can lose a centime.
const RATE_INDIVIDUAL = 66;
const RATE_ORGANIZATION = 60;
const RATE_COLUCHE = 75;
/**
 * Blank is permissive: the statement renders on StepAmount, before StepDetails collects the country. Any other
 * value must be FR, because a French reduction shown to a donor living elsewhere is a false statement about their
 * taxes.
 */
function donorCountryAllowsFrenchRules(country) {
    return country.trim() === "" || country === "FR";
}
/**
 * One `Math.floor` on the total reduction, the net derived as `amount - reduction`: the displayed net cost can only
 * be too high, by at most one centime, never too low, which would overstate the tax benefit.
 */
function reductionMinor(amountMinor, variant) {
    switch (variant) {
        case "Organization":
            return Math.floor((amountMinor * RATE_ORGANIZATION) / 100);
        // The sentence states the two-tier Coluche rule, so the figure is the two-tier sum. The IFI relief is on another
        // tax and never adds to it.
        case "Coluche":
        case "ColucheIFI": {
            const capped = Math.min(amountMinor, COLUCHE_THRESHOLD_MINOR);
            const excess = amountMinor - capped;
            return Math.floor((capped * RATE_COLUCHE + excess * RATE_INDIVIDUAL) / 100);
        }
        case "IFI":
        case "Individual":
            return Math.floor((amountMinor * RATE_INDIVIDUAL) / 100);
    }
}
/** Organization wins outright: the IFI and Coluche reliefs apply to individuals only. */
function pickVariant(donorType, eligibleIFI, eligibleColuche) {
    if (donorType === "Organization") {
        return "Organization";
    }
    if (eligibleIFI && eligibleColuche) {
        return "ColucheIFI";
    }
    if (eligibleIFI) {
        return "IFI";
    }
    if (eligibleColuche) {
        return "Coluche";
    }
    return "Individual";
}
/**
 * @param country `state.country`: an ISO 3166-1 alpha-2 code, or '' before StepDetails
 */
export function resolveTaxReduction(settings, currency, donorType, country, amountMinor) {
    if (settings.enableTaxReduction !== true) {
        return null;
    }
    // CONSTRAINT: the sentences quote euro figures, and opdon issues a French receipt for a euro
    // donation only (DonationCurrency.TaxReceiptIso): any other currency would be promised a reduction.
    if (currency !== "EUR") {
        return null;
    }
    const taxCountry = settings.taxResidenceCountry ?? "FR";
    if (taxCountry !== "FR") {
        return null;
    }
    if (!donorCountryAllowsFrenchRules(country)) {
        return null;
    }
    // Reachable: the free amount is 0 until typed, and a frequency without presets seeds 0.
    if (!Number.isFinite(amountMinor) || amountMinor <= 0) {
        return null;
    }
    const variant = pickVariant(donorType, settings.eligibleIFI === true, settings.eligibleColuche === true);
    return {
        variant,
        amountMinor,
        netMinor: amountMinor - reductionMinor(amountMinor, variant),
    };
}
